/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser, Undo2, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * SignaturePad — tavoletta di firma touch/penna per tablet (Pointer Events).
 *
 * - Canvas ad alta densità (devicePixelRatio) per firme nitide.
 * - Cattura dati biometrici leggeri (n. tratti, durata, pressione media) utili
 *   come evidenza FEA insieme all'impronta del documento.
 * - `onChange({ dataUrl, strokes, durationMs, isEmpty })` ad ogni tratto.
 */
export const SignaturePad = ({ height = 180, onChange, disabled = false, label = 'Firma qui', className, testId = 'signature-pad' }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const strokes = useRef([]); // array di array di punti {x,y,p,t}
  const current = useRef([]);
  const startedAt = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const getCtx = () => canvasRef.current?.getContext('2d');

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = document.documentElement.classList.contains('dark') ? '#e4e4e7' : '#18181b';
    ctx.lineWidth = 2.2;
    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  const redraw = () => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    for (const s of strokes.current) {
      ctx.beginPath();
      s.forEach((pt, i) => { if (i === 0) ctx.moveTo(pt.x, pt.y); else ctx.lineTo(pt.x, pt.y); });
      ctx.stroke();
    }
  };

  useEffect(() => {
    setupCanvas();
    const ro = new ResizeObserver(() => setupCanvas());
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [setupCanvas]);

  const emit = () => {
    const canvas = canvasRef.current;
    const empty = strokes.current.length === 0;
    setIsEmpty(empty);
    const pts = strokes.current.flat();
    const avgP = pts.length ? pts.reduce((a, p) => a + (p.p || 0.5), 0) / pts.length : 0;
    onChange?.({
      dataUrl: empty ? null : canvas.toDataURL('image/png'),
      strokes: strokes.current.length,
      points: pts.length,
      durationMs: startedAt.current ? Date.now() - startedAt.current : 0,
      avgPressure: Number(avgP.toFixed(3)),
      isEmpty: empty,
    });
  };

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, p: e.pressure || 0.5, t: Date.now() };
  };

  const onDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    if (!startedAt.current) startedAt.current = Date.now();
    const p = pos(e);
    current.current = [p];
    const ctx = getCtx();
    ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + 0.01, p.y + 0.01); ctx.stroke();
  };
  const onMove = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = getCtx();
    const last = current.current[current.current.length - 1];
    ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    current.current.push(p);
  };
  const onUp = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
    if (current.current.length) strokes.current.push(current.current);
    current.current = [];
    emit();
  };

  const clear = () => { strokes.current = []; startedAt.current = null; redraw(); emit(); };
  const undo = () => { strokes.current.pop(); redraw(); emit(); };

  return (
    <div className={cn('space-y-2', className)} data-testid={testId}>
      <div className={cn('relative rounded-xl border-2 border-dashed bg-card overflow-hidden touch-none', isEmpty ? 'border-border' : 'border-primary/50', disabled && 'opacity-60')}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height }}
          className="block cursor-crosshair"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          onPointerCancel={onUp}
          aria-label={label}
          role="img"
          data-testid={`${testId}-canvas`}
        />
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground gap-2">
            <PenLine className="h-4 w-4" /> {label}
          </div>
        )}
        <div className="pointer-events-none absolute left-4 right-4 bottom-8 border-b border-muted-foreground/30" />
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={undo} disabled={isEmpty || disabled} className="touch-target" data-testid={`${testId}-undo`}><Undo2 className="h-4 w-4" /> Annulla tratto</Button>
        <Button type="button" variant="ghost" size="sm" onClick={clear} disabled={isEmpty || disabled} className="touch-target" data-testid={`${testId}-clear`}><Eraser className="h-4 w-4" /> Cancella</Button>
      </div>
    </div>
  );
};

export default SignaturePad;
